import { MoreHorizontal } from 'lucide-react';
import type { ChannelCloseoutListItem } from '@/shared/api';
import { ActionMenu, type ActionMenuItem } from '@/shared/components';
import { OtaLogo } from '@/features/properties/OtaLogo';
import { cn } from '@/shared/lib/cn';
import {
  closeoutNightsLabel,
  closeoutErrorText,
  closeoutStatusClass,
  closeoutStatusLabel,
  formatCloseoutDateRange,
  RETRYABLE_CLOSEOUT_ERRORS,
} from './channelCloseoutPresentation';

export type CloseoutAction = 'retry' | 'reconcile' | 'reissue' | 'remove';

/** Welche Aktion der Zustand der Regel nahelegt — höchstens eine, sichtbar neben dem Status. */
export function nextCloseoutAction(
  item: ChannelCloseoutListItem,
): { kind: Exclude<CloseoutAction, 'remove'>; label: string } | null {
  if (item.syncStatus === 'Failed' && item.lastErrorCode === 'create_outcome_unknown') {
    return { kind: 'reissue', label: 'Abwesenheit bestätigen' };
  }
  // Nur was der Server annimmt; sonst endete der Klick in einer Absage.
  if (item.syncStatus === 'Failed') {
    return item.lastErrorCode && RETRYABLE_CLOSEOUT_ERRORS.includes(item.lastErrorCode)
      ? { kind: 'retry', label: 'Erneut senden' }
      : null;
  }
  if (item.syncStatus === 'NeedsReconcile' || item.syncStatus === 'Drifted') {
    return { kind: 'reconcile', label: 'Abgleichen' };
  }
  return null;
}

export function ChannelCloseoutRow({
  item,
  canManage,
  busy,
  onEdit,
  onShowInCalendar,
  onAction,
}: {
  item: ChannelCloseoutListItem;
  canManage: boolean;
  busy: boolean;
  onEdit: () => void;
  onShowInCalendar: () => void;
  onAction: (action: CloseoutAction) => void;
}) {
  const action = nextCloseoutAction(item);
  const error = closeoutErrorText(item.lastErrorCode, item.syncStatus === 'Failed');
  const editable = canManage && item.desiredState === 'Active';
  const menu: ActionMenuItem[] = [
    // Bearbeiten beträfe auch aufgehobene Regeln — der Server setzte sie dabei wieder aktiv.
    ...(editable ? [{ label: 'Bearbeiten', icon: 'Edit' as const, onClick: onEdit }] : []),
    { label: 'Im Grundkalender zeigen', icon: 'CalendarDays', onClick: onShowInCalendar },
    ...(editable
      ? [
          {
            label: 'Aufheben',
            icon: 'Trash' as const,
            destructive: true,
            onClick: () => onAction('remove'),
          },
        ]
      : []),
  ];
  return (
    <article className="border-hairline flex flex-wrap items-center gap-x-4 gap-y-3 rounded-[18px] border px-4 py-3.5">
      <div className="flex min-w-0 flex-[1_1_260px] flex-col gap-1">
        <div className="flex items-center gap-2">
          {item.channels.map((channel) => (
            <OtaLogo key={channel} channel={channel} size={18} />
          ))}
          <h3 className="text-ink min-w-0 truncate text-[15px] font-bold">{item.name}</h3>
        </div>
        <p className="text-ink-soft text-[13px]">
          {formatCloseoutDateRange(item.startDate, item.endDate)} ·{' '}
          {closeoutNightsLabel(item.startDate, item.endDate)}
        </p>
        {item.apartments.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.apartments.map((apartment) => (
              <span
                key={apartment.propertyId}
                className="bg-card-alt text-ink-soft rounded-pill px-2 py-0.5 text-[11px] font-semibold"
              >
                {apartment.name}
              </span>
            ))}
          </div>
        )}
        {error && (
          <p
            className={cn(
              'text-xs',
              item.syncStatus === 'Failed' ? 'text-sealwax' : 'text-ink-soft',
            )}
          >
            {error}
          </p>
        )}
      </div>
      <div className="flex min-w-[150px] flex-col items-start gap-1">
        <span
          className={`rounded-pill inline-flex h-[22px] items-center px-2.5 text-[11px] font-bold ${closeoutStatusClass(item.syncStatus)}`}
        >
          {closeoutStatusLabel(item.syncStatus)}
        </span>
        {item.desiredState === 'Deleted' && (
          <span className="text-ink-soft text-[11px]">wird aufgehoben</span>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        {canManage && action && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onAction(action.kind)}
            className="rounded-pill bg-forest hover:bg-forest-deep h-11 px-4 text-[13px] font-bold text-white disabled:opacity-50"
          >
            {action.label}
          </button>
        )}
        <ActionMenu
          items={menu}
          trigger={
            <button
              type="button"
              aria-label={`Aktionen für ${item.name}`}
              className="rounded-pill border-hairline text-ink hover:bg-card-alt grid size-11 place-items-center border"
            >
              <MoreHorizontal size={18} aria-hidden />
            </button>
          }
        />
      </div>
    </article>
  );
}
